/**
 * v2 API contract of contracts (the «Договор» tab of client and person cards, contract upload with
 * Claude): docs/research/design2-records.md §6, §8 R9/R13/R15; decisions-log V2, V17.
 */
import { z } from 'zod';
import { isIsoDate } from '../dates';
import { CURRENCIES } from '../money';

/**
 * Which day's central-bank rate an invoice issued in another currency than the price is paid at
 * (V185): the invoice date, the payment day, the day before payment, the last day of the period.
 */
export const FX_RULES = ['invoice_date', 'payment_date', 'day_before_payment', 'period_end'] as const;
export type FxRule = (typeof FX_RULES)[number];

export const FX_RULE_LABELS: Readonly<Record<FxRule, string>> = {
  invoice_date: 'На дату счёта',
  payment_date: 'На дату оплаты',
  day_before_payment: 'На день до оплаты',
  period_end: 'На последний день периода',
};

/**
 * Conditions read from a contract (V253): only what the system acts on — the number, the dates, the
 * probation (the оклад steps follow it, V238), whether the person is a молодой специалист and until
 * when the deduction holds (V256), our side (matched to our entity, V238) and the money terms. Older
 * stored terms with other keys are ignored.
 */
export const CONTRACT_TERM_KEYS = [
  'number',
  'signed',
  'start',
  'end',
  'probation',
  'youngSpecialist',
  'youngSpecialistUntil',
  'ourParty',
  'billing',
  'rate',
  'cur',
  'terms',
] as const;
export type ContractTermKey = (typeof CONTRACT_TERM_KEYS)[number];

/**
 * A step of the оклад by an employment contract (V238): the monthly gross from a date — the reduced
 * оклад of the probation from its start, the full one from the day after it.
 */
export interface ContractSalaryStep {
  readonly from: string;
  readonly grossMinor: number;
}

export const contractSalaryStepSchema = z.object({
  from: z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД'),
  grossMinor: z.number().int().positive('Оклад — число больше 0'),
});

/** At most this many steps of the оклад in one contract. */
export const CONTRACT_SALARY_STEPS_MAX = 12;

/** Keys holding a `YYYY-MM-DD` date (date inputs in the review form). */
export const CONTRACT_DATE_KEYS: readonly ContractTermKey[] = ['signed', 'start', 'end', 'youngSpecialistUntil'];

/** Free-text values by key; an absent or empty value is «не найдено — указать». */
export type ContractTerms = Partial<Record<ContractTermKey, string>>;

/** Days before the end when a contract reads «ещё N дн.» (R9). */
export const CONTRACT_EXPIRING_DAYS = 30;
/** «Продлить на год» moves the end by 365 days (V17). */
export const CONTRACT_EXTEND_DAYS = 365;

export const CONTRACT_PARTY_KINDS = ['client', 'person'] as const;
export type ContractPartyKind = (typeof CONTRACT_PARTY_KINDS)[number];

export interface ContractPartyRef {
  readonly kind: ContractPartyKind;
  readonly id: string;
}

export const contractPartySchema = z.object({
  kind: z.enum(CONTRACT_PARTY_KINDS),
  id: z.string().min(1),
});

/** GET /contracts?clientId= | ?personId= */
export const contractsQuerySchema = z
  .object({ clientId: z.string().min(1).optional(), personId: z.string().min(1).optional() })
  .refine((q) => !!q.clientId !== !!q.personId, 'Укажите клиента или человека');
export type ContractsQuery = z.infer<typeof contractsQuerySchema>;

export interface ContractFile {
  readonly documentId: string;
  readonly name: string;
  readonly sizeBytes: number;
  /** PDFs and images open inline in the preview; other files download. */
  readonly previewable: boolean;
}

/** A salary row written by the contract (V238): the оклад and what it leaves on hand. */
export interface ContractSalaryView {
  readonly id: string;
  readonly from: string;
  readonly grossMinor: number;
  /** The оклад less the entity's withheld taxes. */
  readonly netMinor: number;
  readonly currency: string;
}

/** What the contract covers: client projects (R5 links) or the contractor's assignments (R15). */
export interface ContractLinkedItem {
  readonly id: string;
  readonly name: string;
  /** Project: status; assignment: its term. */
  readonly status: 'active' | 'paused' | 'ended' | null;
  readonly from: string | null;
  readonly to: string | null;
}

export interface ContractItem {
  readonly id: string;
  readonly kind: 'client' | 'contractor';
  readonly clientId: string | null;
  readonly personId: string | null;
  readonly partyName: string;
  readonly number: string;
  readonly startOn: string | null;
  readonly endOn: string | null;
  readonly currency: string;
  /** Terminated or past its end (R9). */
  readonly ended: boolean;
  /** Days left when 0–30 and not ended; null otherwise. */
  readonly daysLeft: number | null;
  /** «Наше юрлицо»; null = not chosen. */
  readonly issuerId: string | null;
  /** Client contract (V185): invoices are issued in this currency; null = the project price's. */
  readonly invoiceCurrency: string | null;
  /** Client contract (V185): the rate an invoice in another currency is paid at; null = on the invoice date. */
  readonly fxRule: FxRule | null;
  readonly file: ContractFile | null;
  readonly terms: ContractTerms;
  readonly linked: readonly ContractLinkedItem[];
  /** Person contract with our entity: the оклад steps it wrote (V238), oldest first. */
  readonly salaries: readonly ContractSalaryView[];
}

export interface ContractIssuerOption {
  readonly id: string;
  /** «{name} · {country}». */
  readonly label: string;
}

export interface PartyContractsResponse {
  readonly party: ContractPartyRef & { readonly name: string };
  /** Sorted by start, newest first. */
  readonly items: readonly ContractItem[];
  readonly issuerOptions: readonly ContractIssuerOption[];
  /** The entity the client's projects point at (`entOf`), shown in the empty «Наше юрлицо» option. */
  readonly inferredIssuerName: string | null;
}

/** POST /contracts/extract (multipart: `file` plus `clientId` or `personId`). */
export const contractExtractFieldsSchema = z
  .object({ clientId: z.string().min(1).optional(), personId: z.string().min(1).optional() })
  .refine((q) => !!q.clientId !== !!q.personId, 'Укажите клиента или человека');

/** Why a contract was not read: no API key, a format Claude cannot take, or the call failed. */
export const CONTRACT_EXTRACT_SKIPS = ['no_key', 'unreadable', 'failed'] as const;
export type ContractExtractSkip = (typeof CONTRACT_EXTRACT_SKIPS)[number];

export interface ContractExtractResponse {
  /** The stored file, kept hidden until the contract is saved. */
  readonly documentId: string;
  readonly fileName: string;
  readonly sizeBytes: number;
  /** False without an API key, for an unreadable file or when the call failed: the form opens empty. */
  readonly extracted: boolean;
  /** Why it was not read, shown above the empty form; null when read. */
  readonly skipped: ContractExtractSkip | null;
  /** Every key, '' when not found. */
  readonly fields: Readonly<Record<ContractTermKey, string>>;
  /** Where each value was found («стр. 1, шапка», «п. 2.1»). */
  readonly sources: ContractTerms;
  /** Our entity matched by «Наша сторона»; null = not found (V238). */
  readonly issuerId: string | null;
  /** Person contract: the оклад steps read from it, oldest first (V238). */
  readonly salaries: readonly ContractSalaryStep[];
}

const termValue = z.string().trim().max(2000);

/** POST /contracts — the reviewed contract. */
export const contractCreateSchema = z
  .object({
    party: contractPartySchema,
    documentId: z.string().min(1).nullable(),
    terms: z.object(Object.fromEntries(CONTRACT_TERM_KEYS.map((key) => [key, termValue.optional()]))) as z.ZodType<ContractTerms, ContractTerms>,
    /** «Наше юрлицо»; for a person, the entity the person is employed in (V238). */
    issuerId: z.string().min(1).nullable().default(null),
    /** Person contract: the оклад steps, written as the salary through `issuerId` (V238). */
    salaries: z.array(contractSalaryStepSchema).max(CONTRACT_SALARY_STEPS_MAX).default([]),
  })
  .refine((b) => b.salaries.length === 0 || b.party.kind === 'person', { message: 'Оклад — только в договоре с человеком', path: ['salaries'] })
  .refine((b) => b.salaries.length === 0 || b.issuerId != null, { message: 'Укажите юрлицо, где оформлен человек', path: ['issuerId'] });
export type ContractCreateInput = z.input<typeof contractCreateSchema>;
export type ContractCreateBody = z.output<typeof contractCreateSchema>;

export const contractIssuerSchema = z.object({ issuerId: z.string().min(1).nullable() });

/** PUT /contracts/:id/young-specialist (V256): whether the person is a молодой специалист and until when the deduction holds. */
export const contractYoungSpecialistSchema = z.object({
  isYoungSpecialist: z.boolean(),
  until: z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД').nullable().default(null),
});
export type ContractYoungSpecialistInput = z.input<typeof contractYoungSpecialistSchema>;

/** PUT /contracts/:id/invoicing — the invoice currency and the rate rule of a client contract (V185). */
export const contractInvoicingSchema = z
  .object({
    invoiceCurrency: z.enum(CURRENCIES).nullable().optional(),
    fxRule: z.enum(FX_RULES).nullable().optional(),
  })
  .strict()
  .refine((b) => (b.invoiceCurrency === undefined) !== (b.fxRule === undefined), 'Измените одно поле');
export type ContractInvoicingInput = z.infer<typeof contractInvoicingSchema>;
export type ContractIssuerInput = z.infer<typeof contractIssuerSchema>;

export interface ContractWriteResult {
  readonly message: string;
  readonly undoToken: string;
}

export interface ContractCreateResult extends ContractWriteResult {
  readonly id: string;
}

export const isContractDate = (value: string | undefined): value is string => !!value && isIsoDate(value);
